import { Check, Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { answerLanguageLabel, sameLanguage } from "../lib/answerLanguages";
import { Throbber } from "./ui/throbber";

interface AnswerLanguageSelectProps {
  /** The offered languages, in order (spec rule 2.6). */
  languageOptions: string[];
  /** Rule 2.1: the control always shows the selected language. */
  selectedLanguage: string;
  /** True while a translation is in flight (rule 4.9). */
  isTranslating: boolean;
  selectLanguage: (code: string) => void;
  /** Tooltip and accessible name; the answer's by default. */
  title?: string;
}

/**
 * Language control for the proposed answer, shared by Venus and Neptune so both
 * surfaces offer the same languages under the same labels and behave identically
 * (rule 2.7). Sits on the answer's header line next to the version history.
 *
 * Rendering is the caller's decision only in where it goes: whether it appears at
 * all follows rule 6.3 and is decided by `useProposedAnswer`
 * (`languageControlVisible`).
 */
export function AnswerLanguageSelect({
  languageOptions,
  selectedLanguage,
  isTranslating,
  selectLanguage,
  title,
}: AnswerLanguageSelectProps) {
  const { t, i18n } = useTranslation();
  const label = title ?? t("answerLanguage.title");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        title={label}
        aria-label={label}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
      >
        {isTranslating ? (
          <Throbber className="h-4 w-4" />
        ) : (
          <Languages className="h-4 w-4" />
        )}
        <span className="uppercase tabular-nums">{selectedLanguage}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {languageOptions.map((code) => {
          const isCurrent = sameLanguage(code, selectedLanguage);
          return (
            <DropdownMenuItem
              key={code}
              onSelect={() => selectLanguage(code)}
              className={
                isCurrent ? "font-medium bg-accent/60 focus:bg-accent" : "pl-7"
              }
            >
              {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
              {answerLanguageLabel(code, i18n.language)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
